<template>
  <div class="booking-customer-page">
    <div class="header-card">
      <h2>My Bookings</h2>
      <p>Active and upcoming trips for your organization.</p>
    </div>

    <div class="filters-card">
      <label>
        From
        <input type="date" v-model="filters.fromDate" />
      </label>
      <label>
        To
        <input type="date" v-model="filters.toDate" />
      </label>
      <label>
        Status
        <select v-model="filters.status">
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
        </select>
      </label>
      <label class="search-label">
        Booking ID
        <input type="text" v-model="filters.booking_search" placeholder="Search by booking ID" />
      </label>
    </div>

    <div class="summary-row" v-if="summary">
      <span><b>{{ summary.count || 0 }}</b> booking(s)</span>
      <span>{{ filters.fromDate }} to {{ filters.toDate }}</span>
      <span>Status: {{ filters.status }}</span>
    </div>

    <div v-if="loading" class="loading-wrap">
      <b-spinner />
    </div>

    <div v-else class="booking-list-wrap">
      <div v-if="items.length === 0" class="empty-card">
        No bookings found for this date range.
      </div>
      <div v-for="row in items" :key="row.booking_id" class="booking-card" @click="openDetail(row)">
        <div class="card-top">
          <div class="card-title-block">
            <div class="booking-id">Booking #{{ row.booking_id }}</div>
            <div class="trip-name" v-if="row.group_name">{{ row.group_name }}</div>
            <div class="subtext">{{ row.org_name || 'Organization unavailable' }}</div>
          </div>
          <div class="card-badges">
            <span v-if="row.confirmation_status" class="status-pill" :class="row.confirmation_status">{{ row.confirmation_status }}</span>
            <span class="status-pill" :class="row.status">{{ row.status }}</span>
          </div>
        </div>

        <div class="card-meta-row">
          <span class="meta-item">📅 {{ formatDateRange(row.start_time, row.finish_time) }}</span>
          <span class="legs-pill" v-if="row.legs && row.legs.length > 1">{{ row.legs.length }} legs</span>
        </div>

        <div class="route-summary" v-if="row.pickup_location || row.dropoff_location">
          <span class="location">{{ row.pickup_location || '?' }}</span>
          <span class="arrow"> → </span>
          <span class="location">{{ row.dropoff_location || '?' }}</span>
          <span v-if="row.legs && row.legs.length > 1" class="more-legs"> (+ more)</span>
        </div>

        <div class="card-footer-row">
          <span class="payment-badge" :class="paymentClass(row.payment_status)" v-if="row.payment_status">
            {{ row.payment_status }}
          </span>
          <span class="driver-info" v-if="row.driver_name || row.vehicle_id">
            <span class="driver-avatar" v-if="row.driver_name">{{ driverInitials(row.driver_name) }}</span>
            {{ row.driver_name || '' }}<span v-if="row.vehicle_id"> · Vehicle {{ row.vehicle_id }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { tbn_get_booking_customer_list } from '../networking/tbn_apis'
import bookingCustomerHelpers from '../mixins/booking_customer_helpers'

const offsetDays = (days) => {
  const dt = new Date()
  dt.setDate(dt.getDate() + days)
  return dt.toISOString().slice(0, 10)
}

export default {
  name: 'booking_customer_list_tbn',
  mixins: [bookingCustomerHelpers],
  data() {
    return {
      loading: false,
      summary: null,
      items: [],
      _initialized: false,
      filters: {
        fromDate: offsetDays(-1),
        toDate: offsetDays(30),
        status: 'all',
        booking_search: '',
      },
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.profile.user,
    }),
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (!this._initialized) return
        clearTimeout(this._filterDebounce)
        this._filterDebounce = setTimeout(() => this.loadData(), 400)
      },
    },
  },
  async created() {
    await this.loadData()
    this._initialized = true
  },
  methods: {
    async loadData() {
      try {
        this.loading = true
        const params = { ...this.filters }
        const { value: { data: listData = {} } } = await tbn_get_booking_customer_list(this.user.tenant, params)
        this.summary = listData.summary || {}
        this.items = listData.results || []
      } catch (e) {
        this.$emit('showAlert', 'Unable to load booking customer data. Please try again.', 'danger', 5000)
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.$router.push({ name: 'booking_customer_detail_tbn', params: { tbn_booking_hash: row.tbn_booking_hash } })
    },
  },
}
</script>

<style scoped>
.booking-customer-page { margin: 16px; }
.header-card, .filters-card, .empty-card { background: #f8f9fb; border: 1px solid #d9dce3; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.header-card h2 { margin: 0; }
.header-card p { margin: 4px 0 0; color: #566; }
.filters-card { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; text-align: left; }
.filters-card label { display: flex; flex-direction: column; font-weight: 600; font-size: 13px; }
.filters-card input, .filters-card select { min-width: 140px; padding: 6px; }
.search-label input { min-width: 220px; }
.summary-row { display: flex; justify-content: space-between; flex-wrap: wrap; margin: 8px 0 12px; font-weight: 600; color: #2d3a4a; }
.booking-list-wrap { max-height: 70vh; overflow: auto; }
.booking-card { text-align: left; border: 1px solid #d9dce3; border-radius: 8px; padding: 12px; margin-bottom: 10px; background: white; cursor: pointer; transition: box-shadow 0.15s; }
.booking-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
.card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; gap: 8px; }
.card-title-block { flex: 1; }
.booking-id { font-weight: 700; font-size: 15px; color: #1a2533; }
.trip-name { font-size: 13px; font-weight: 600; color: #2d3a4a; margin-top: 2px; }
.subtext { color: #5b6a79; font-size: 12px; margin-top: 2px; }
.card-badges { display: flex; flex-direction: column; gap: 4px; align-items: flex-end; }
.status-pill { text-transform: uppercase; font-size: 10px; padding: 3px 8px; border-radius: 10px; font-weight: 700; color: white; white-space: nowrap; }
.status-pill.active { background: #198754; }
.status-pill.upcoming { background: #0d6efd; }
.status-pill.completed { background: #6c757d; }
.status-pill.confirmed { background: #198754; }
.card-meta-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; font-size: 13px; color: #495057; }
.legs-pill { background: #e9ecef; color: #495057; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
.route-summary { font-size: 13px; color: #2d3a4a; margin-bottom: 8px; }
.location { font-weight: 600; }
.arrow { color: #adb5bd; }
.more-legs { color: #6c757d; font-size: 12px; }
.card-footer-row { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 6px; border-top: 1px solid #f0f2f5; padding-top: 8px; }
.payment-badge { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 10px; }
.payment-paid { background: #d1e7dd; color: #0a3622; }
.payment-due { background: #f8d7da; color: #58151c; }
.payment-deposit { background: #fff3cd; color: #664d03; }
.payment-other { background: #e9ecef; color: #495057; }
.driver-info { font-size: 12px; color: #495057; display: flex; align-items: center; gap: 6px; }
.driver-avatar { background: #0d6efd; color: white; font-size: 10px; font-weight: 700; width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; }
.loading-wrap { padding: 24px; }
</style>
